import { NgTemplateOutlet } from '@angular/common';
import { Component, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';

/**
 * Read-only Formly field type that renders arbitrary content via an `ng-template`
 * passed through `props['template']`. No form control value is read or written.
 *
 * Props:
 * - `template` – `TemplateRef<any>`  The template to render. Template context is
 *                                    supplied via the optional `context` prop.
 * - `context`  – `any`              Optional context object forwarded as the template
 *                                    context (i.e. `*ngTemplateOutlet="...; context: context"`).
 *
 * ⚠️ IMPORTANT — Because `template` is a `TemplateRef`, the host component MUST
 * build the field config in `ngAfterViewInit` (not `ngOnInit`), after `@ViewChild`
 * has resolved the template reference.
 *
 * Usage in host component:
 * ```typescript
 * \@ViewChild('previewTpl') previewTplRef!: TemplateRef<any>;
 *
 * fields: FormlyFieldConfig[] = [];
 *
 * ngAfterViewInit(): void {
 *   this.fields = [{
 *     // No key — read-only display; nothing is written to the model
 *     type: 'template-content',
 *     props: {
 *       template: this.previewTplRef,
 *       context: { someValue: 42 },
 *     },
 *   }];
 * }
 * ```
 *
 * Usage in host template:
 * ```html
 * <ng-template #previewTpl let-someValue="someValue">
 *   <p>The value is {{ someValue }}</p>
 * </ng-template>
 * ```
 */
@Component({
  selector: 'app-template-content-formly-type',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './template-content-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './template-content-formly-type.component.css',
})
export class TemplateContentFormlyTypeComponent extends FieldType<FieldTypeConfig> {
  get template(): TemplateRef<any> | null {
    return (this.props['template'] as TemplateRef<any>) ?? null;
  }

  get context(): any {
    return (this.props['context'] as any) ?? {};
  }
}
